import React from "react";
import { Link } from "react-router-dom";
import "./Education.css";
import { navigateToHomeSection } from '../utils/navigation';

const navLinks = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];
const footerLinks = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];

const metaCards = [
  { label: "SECTOR", value: "Education" },
  { label: "FOCUS", value: "Risk · Ops · Customer" },
  { label: "MATURITY", value: "Production-grade" },
  { label: "COVERAGE", value: "EU & UK" },
];

const plays = [
  { id: "01", title: "AI Marking", desc: "Powered by aivionGrade for handwritten and digital assessment at scale." },
  { id: "02", title: "Student Success Analytics", desc: "Early-warning models for retention, attainment and wellbeing." },
  { id: "03", title: "Adaptive Learning", desc: "Personalised learning paths based on mastery and engagement signals." },
  { id: "04", title: "Admissions & Operations", desc: "AI for application triage, scholarship matching and timetabling." },
  { id: "05", title: "Research Acceleration", desc: "GenAI tools for literature review, data analysis and grant writing." },
  { id: "06", title: "Responsible AI in Education", desc: "Frameworks for ethical, transparent use of AI with students." },
];

const phases = [
  { phase: "PHASE 01", title: "Co-Design", desc: "Work side-by-side with faculty and learning designers.", align: "left" },
  { phase: "PHASE 02", title: "Pilot", desc: "Run on one programme or cohort with rigorous evaluation.", align: "right" },
  { phase: "PHASE 03", title: "Scale", desc: "Roll out across faculties with governance and training.", align: "left" },
  { phase: "PHASE 04", title: "Evaluate", desc: "Measure pedagogical and operational impact term over term.", align: "right" },
];

const outcomes = [
  { num: "/ 01", text: "Significant marking-time savings released back to teaching" },
  { num: "/ 02", text: "Earlier intervention with at-risk students" },
  { num: "/ 03", text: "Improved student outcomes and satisfaction" },
  { num: "/ 04", text: "Operational efficiency in admissions, timetabling and student services" },
];

const otherSectors = [
  { name: "Energy & Utilities", desc: "AI for the energy transition", img: "energy", link: "/energy-utilities" },
  { name: "Financial Services", desc: "AI for banking, lending and insurance", img: "financial", link: "/financial-services" },
  { name: "Healthcare", desc: "Trustworthy AI for clinical and operational excellence", img: "health", link: "/healthcare" },
];

export default function Education() {
  return (
    <div className="edu-root">

      {/* ── HERO  ── */}
      <section className="edu-hero">
        <nav className="edu-nav">
          <div className="edu-nav-inner">
            <div className="edu-logo">
              <span className="edu-logo-aiv">AIV</span><span className="edu-logo-ion">ION</span>
            </div>
            <ul className="edu-nav-links">
              {navLinks.map((l) => <li key={l.label}><a href={l.href} onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a></li>)}
            </ul>
            <a href="/#contact" className="edu-contact-btn" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>CONTACT US</a>
          </div>
        </nav>

        <div className="edu-hero-content">
          <p className="edu-hero-breadcrumb">&#8592; INDUSTRIES</p>
          <div className="edu-hero-sector">
            <span className="edu-sector-icon">&#127974;</span>
            <span>SECTOR FOCUS</span>
          </div>
          <h1 className="edu-hero-title">Education</h1>
          <p className="edu-hero-sub">AI for higher education and learning</p>
        </div>
      </section>

      {/* ── META CARD ROW (overlapping hero/white) ── */}
      <div className="edu-meta-wrap">
        <div className="edu-meta-row">
          {metaCards.map((m) => (
            <div key={m.label} className="edu-meta-card">
              <p className="edu-meta-label">{m.label}</p>
              <p className="edu-meta-value">{m.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── OPPORTUNITY SECTION ── */}
      <section className="edu-opportunity">
        <div className="edu-opportunity-inner">
          <div className="edu-opp-left">
            <p className="edu-eyebrow">THE OPPORTUNITY</p>
            <h2 className="edu-opp-title">Where AI is changing Education.</h2>
            <span className="edu-opp-pill">&#9679; AIVION sector practice</span>
          </div>
          <div className="edu-opp-right">
            <p className="edu-dropcap-para">
              <span className="edu-dropcap">A</span>VION works with universities, awarding bodies and edtech providers to apply AI where it has the greatest pedagogical and operational impact: faster, fairer assessment; earlier identification of at-risk students; and more personalised learning experiences. Our solutions are co-designed with academic and learning-design teams to ensure they augment — never replace — educator expertise.
            </p>
            <blockquote className="edu-quote">
              <span className="edu-quote-mark">&#8220;</span>
              <p>AI for higher education and learning</p>
              <cite>AIVION — EDUCATION PRACTICE</cite>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ── SIX HIGH-IMPACT PLAYS ── */}
      <section className="edu-plays">
        <div className="edu-plays-inner">
          <div className="edu-plays-header">
            <div>
              <p className="edu-eyebrow">WHERE WE WORK</p>
              <h2 className="edu-plays-title">Six high-impact plays</h2>
            </div>
            <span className="edu-plays-count">06 AREAS</span>
          </div>
          <div className="edu-plays-list">
            {plays.map((p) => (
              <div key={p.id} className="edu-play-row">
                <span className="edu-play-num">{p.id}</span>
                <h4 className="edu-play-title">{p.title}</h4>
                <p className="edu-play-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ENGAGEMENT MODEL — ZIGZAG PHASES ── */}
      <section className="edu-phases">
        <div className="edu-phases-inner">
          <p className="edu-eyebrow edu-center">ENGAGEMENT MODEL</p>
          <h2 className="edu-phases-title edu-center">From assessment to lasting capability</h2>
          <div className="edu-phases-track">
            {phases.map((ph) => (
              <div key={ph.phase} className={`edu-phase-row edu-phase-${ph.align}`}>
                <div className="edu-phase-card">
                  <p className="edu-phase-label">{ph.phase}</p>
                  <h4 className="edu-phase-name">{ph.title}</h4>
                  <p className="edu-phase-desc">{ph.desc}</p>
                </div>
                <span className="edu-phase-dot" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUTCOMES (dark) ── */}
      <section className="edu-outcomes">
        <div className="edu-outcomes-inner">
          <p className="edu-eyebrow">THE IMPACT</p>
          <h2 className="edu-outcomes-title">Outcomes our Education clients see.</h2>
          <div className="edu-outcomes-grid">
            {outcomes.map((o) => (
              <div key={o.num} className="edu-outcome-cell">
                <span className="edu-outcome-num">{o.num}</span>
                <p>{o.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OTHER SECTORS ── */}
      <section className="edu-other-sectors">
        <div className="edu-other-inner">
          <div className="edu-other-header">
            <div>
              <p className="edu-eyebrow">OTHER SECTORS</p>
              <h2 className="edu-other-title">Across the industries we serve</h2>
            </div>
            <Link to="/industries" className="edu-other-all">All industries &#8594;</Link>
          </div>
          <div className="edu-other-grid">
            {otherSectors.map((s) => (
              <div key={s.name} className={`edu-sector-card edu-img-${s.img}`}>
                <div className="edu-sector-img" />
                <div className="edu-sector-info">
                  <p className="edu-sector-tag">INDUSTRY</p>
                  <h4>{s.name}</h4>
                  <p className="edu-sector-desc">{s.desc}</p>
                  <Link to={s.link} className="edu-sector-explore">EXPLORE &#8594;</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER (dark) ── */}
      <section className="edu-cta">
        <div className="edu-cta-inner">
          <div className="edu-cta-left">
            <p className="edu-eyebrow">START THE CONVERSATION</p>
            <h2 className="edu-cta-title">Bring AIVION's Education expertise to your team.</h2>
            <p className="edu-cta-desc">
              A focused 30-minute briefing with one of our sector leads — exploring the highest-value AI plays for
              your business and how to get there safely.
            </p>
          </div>
          <div className="edu-cta-right">
            <a href="/#contact" className="edu-cta-btn-solid" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>REQUEST A SECTOR BRIEFING &#8594;</a>
            <button className="edu-cta-btn-outline" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#case-studies');}}>SEE SECTOR CASE STUDIES</button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="edu-footer">
        <div className="edu-footer-inner">
          <div className="edu-footer-brand">
            <div className="edu-footer-logo">
              <span className="edu-logo-aiv">AIV</span><span className="edu-logo-ion">ION</span>
            </div>
            <p>Delivering meaningful outcomes through intelligent AI solutions.</p>
            <div className="edu-footer-icons">
              <span className="edu-icon">in</span>
              <span className="edu-icon">&#9993;</span>
            </div>
          </div>
          <div className="edu-footer-col">
            <p className="edu-footer-heading">QUICK LINKS</p>
            <div className="footer-quick-links-grid">
              {footerLinks.map((l) => <a key={l.label} href={l.href} className="edu-footer-link" onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a>)}
            </div>
          </div>
        </div>
        <div className="edu-footer-bottom">
          <p>&#169; 2026 AIVION. All rights reserved.</p>
          
        </div>
      </footer>

    </div>
  );
}
